import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// S16 — Top Operarios Riesgo Extremo (Spec V4 §3.4)
// Layout: LEFT tabla + RIGHT regla de priorización + FOOTER insight
// No rank column. Header teal. IRH badge colored.

const getIrhConfig = (irh) => {
    if (irh == null)   return { bg: 'transparent', text: '#617993' };
    if (irh < 50)      return { bg: '#E74C3C', text: '#fff' };
    if (irh <= 60)     return { bg: '#E67E22', text: '#fff' };
    if (irh < 75)      return { bg: '#D4AC0D', text: '#1a1a1a' };
    return                    { bg: '#E8F8F0', text: '#1E7A4E' };
};

const getIashConfig = (iash) => {
    if (iash == null)  return { bg: 'transparent', text: '#617993' };
    if (iash < 50)     return { bg: '#E74C3C', text: '#fff' };
    if (iash < 60)     return { bg: '#E67E22', text: '#fff' };
    return                    { bg: 'transparent', text: '#617993' };
};

const getIpraSupConfig = (ipra) => {
    if (ipra == null)  return { bg: 'transparent', text: '#617993' };
    if (ipra < 55)     return { bg: '#E74C3C', text: '#fff' };
    if (ipra < 65)     return { bg: '#E67E22', text: '#fff' };
    return                    { bg: 'transparent', text: '#617993' };
};

const getAccion = (irh) => {
    if (irh == null) return { label: '—',           bg: '#9AA2AE', color: '#fff' };
    if (irh < 50)    return { label: 'INMEDIATA',   bg: '#E74C3C', color: '#fff' };
    if (irh <= 60)   return { label: 'Esta semana', bg: '#E67E22', color: '#fff' };
    return                  { label: 'Seguimiento', bg: '#D4AC0D', color: '#1a1a1a' };
};

const ManCrossPageTopQ1 = ({ reportData, headerText, pageNumber }) => {
    const payload      = reportData?.payload || {};
    const rawRows      = payload.top_operarios_q1 || [];
    const totalQ1      = payload.total_q1 ?? 0;
    const qCounts      = payload.irh_quadrant_counts || {};
    const totalScatter = Object.values(qCounts).reduce((s, n) => s + n, 0);

    // Headcount total de planta para el % en el panel derecho
    const totalPlant = useMemo(() => {
        const hba = payload.headcount_by_management_area || {};
        const sum = Object.values(hba).reduce((s, n) => s + (typeof n === 'number' ? n : 0), 0);
        return sum > 0 ? sum : totalScatter;
    }, [payload.headcount_by_management_area, totalScatter]);

    const q1PctPlant   = totalPlant > 0 ? (totalQ1 / totalPlant * 100).toFixed(1) : '—';
    // Concentración de exposición: Q1 / total scatter
    const q1PctScatter = totalScatter > 0 ? Math.round(totalQ1 / totalScatter * 100) : 0;

    const { rows, extra } = useMemo(() => {
        const extra_entry = rawRows.find(r => 'extra' in r && !('codigo' in r));
        const data        = rawRows.filter(r => 'codigo' in r);
        return { rows: data, extra: extra_entry?.extra ?? 0 };
    }, [rawRows]);

    const uniqueSups = useMemo(
        () => new Set(rows.map(r => r.sup_codigo).filter(Boolean)).size,
        [rows]
    );

    const isEmpty = totalQ1 === 0;

    return (
        <div className="mgcr-q1-page">

            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Top Operarios Riesgo Extremo — IRH</h2>
            </div>

            {/* ── Board ── */}
            <div className="mgcr-q1-board">

                {/* LEFT: tabla de exposición */}
                <div className="mgcr-q1-left">
                    {isEmpty ? (
                        <div className="mgcr-q1-empty">
                            No hay operarios en zona crítica Q1 este período.
                        </div>
                    ) : (
                        <>
                            {/* Callout dentro del panel izquierdo */}
                            <div className="mgcr-q1-callout-row">
                                <span className="mgcr-q1-section-label">
                                    Tabla de<br />Exposición
                                </span>
                                <span className="mgcr-q1-callout-text">
                                    {uniqueSups > 0
                                        ? <><strong>{uniqueSups} supervisor{uniqueSups !== 1 ? 'es' : ''}</strong> concentran el {q1PctScatter}% de la exposición total al riesgo — intervención no es negociable.</>
                                        : <>Sin supervisores con múltiples operarios en Q1 este período.</>
                                    }
                                </span>
                            </div>

                            {/* Tabla */}
                            <div className="mgcr-q1-table">
                                <div className="mgcr-q1-row mgcr-q1-head">
                                    <div className="mgcr-q1-cell mgcr-q1-c-cod">Código</div>
                                    <div className="mgcr-q1-cell mgcr-q1-c-area">Área</div>
                                    <div className="mgcr-q1-cell mgcr-q1-c-iash">IASH</div>
                                    <div className="mgcr-q1-cell mgcr-q1-c-perfil">Perfil</div>
                                    <div className="mgcr-q1-cell mgcr-q1-c-sup">Supervisor</div>
                                    <div className="mgcr-q1-cell mgcr-q1-c-iprasup">IPRA Sup</div>
                                    <div className="mgcr-q1-cell mgcr-q1-c-irh">IRH</div>
                                    <div className="mgcr-q1-cell mgcr-q1-c-accion">Acción</div>
                                </div>

                                {rows.map((op, idx) => {
                                    const irhCfg  = getIrhConfig(op.irh ?? null);
                                    const iashCfg = getIashConfig(op.iash ?? null);
                                    const ipraCfg = getIpraSupConfig(op.ipra_sup ?? null);
                                    const accion  = getAccion(op.irh ?? null);
                                    return (
                                        <div key={op.codigo || idx}
                                             className={`mgcr-q1-row${idx % 2 === 0 ? ' mgcr-q1-row-even' : ''}`}>
                                            <div className="mgcr-q1-cell mgcr-q1-c-cod">
                                                <strong>{op.codigo || '—'}</strong>
                                            </div>
                                            <div className="mgcr-q1-cell mgcr-q1-c-area">{op.area || '—'}</div>
                                            <div className="mgcr-q1-cell mgcr-q1-c-iash">
                                                <span className="mgcr-q1-badge" style={{ background: iashCfg.bg, color: iashCfg.text }}>
                                                    {op.iash != null ? Math.round(op.iash) : '—'}
                                                </span>
                                            </div>
                                            <div className="mgcr-q1-cell mgcr-q1-c-perfil">{op.perfil || '—'}</div>
                                            <div className="mgcr-q1-cell mgcr-q1-c-sup">{op.sup_codigo || '—'}</div>
                                            <div className="mgcr-q1-cell mgcr-q1-c-iprasup">
                                                <span className="mgcr-q1-badge" style={{ background: ipraCfg.bg, color: ipraCfg.text }}>
                                                    {op.ipra_sup != null ? Math.round(op.ipra_sup) : '—'}
                                                </span>
                                            </div>
                                            <div className="mgcr-q1-cell mgcr-q1-c-irh">
                                                <span className="mgcr-q1-badge" style={{ background: irhCfg.bg, color: irhCfg.text }}>
                                                    {op.irh != null ? op.irh.toFixed(1) : '—'}
                                                </span>
                                            </div>
                                            <div className="mgcr-q1-cell mgcr-q1-c-accion">
                                                <span className="mgcr-q1-badge mgcr-q1-badge-accion"
                                                      style={{ background: accion.bg, color: accion.color }}>
                                                    {accion.label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}

                                {extra > 0 && (
                                    <div className="mgcr-q1-row mgcr-q1-row-extra">
                                        <div className="mgcr-q1-cell" style={{ flex: 1, justifyContent: 'center', color: '#617993', fontStyle: 'italic' }}>
                                            + {extra} operario{extra !== 1 ? 's' : ''} adicional{extra !== 1 ? 'es' : ''} en intervención Q1
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="keep-blankspace" />

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageTopQ1;
